import { useState, useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { AlertCircle, Save, RotateCcw, Check } from 'lucide-react';
import clsx from 'clsx';
import type { CatalogContent } from '../../api';

interface CatalogJsonEditorProps {
  initialValue: CatalogContent;
  onSave: (content: CatalogContent) => Promise<void>;
  readOnly?: boolean;
  className?: string;
}

export function CatalogJsonEditor({
  initialValue,
  onSave,
  readOnly = false,
  className
}: CatalogJsonEditorProps) {
  const [content, setContent] = useState(() => JSON.stringify(initialValue, null, 2));
  const [hasChanges, setHasChanges] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const initialValueRef = useRef(JSON.stringify(initialValue, null, 2));

  // Keyboard shortcut: Ctrl+S to save
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (isValid && hasChanges && !isSaving && !readOnly) {
          handleSave();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isValid, hasChanges, isSaving, readOnly]);

  // Update initial value when prop changes
  useEffect(() => {
    const newInitial = JSON.stringify(initialValue, null, 2);
    if (newInitial !== initialValueRef.current) {
      initialValueRef.current = newInitial;
      setContent(newInitial);
      setHasChanges(false);
      setIsValid(true);
      setValidationError(null);
    }
  }, [initialValue]);

  const handleEditorDidMount = (editor: editor.IStandaloneCodeEditor) => {
    editorRef.current = editor;

    // Auto-format on mount
    editor.getAction('editor.action.formatDocument')?.run();
  };

  const handleEditorChange = (value: string | undefined) => {
    if (value === undefined) return;

    setContent(value);

    // Check if different from initial
    const changed = value !== initialValueRef.current;
    setHasChanges(changed);

    // Validate JSON
    try {
      JSON.parse(value);
      setIsValid(true);
      setValidationError(null);
    } catch (error) {
      setIsValid(false);
      if (error instanceof Error) {
        setValidationError(error.message);
      }
    }

    // Reset save success indicator
    if (saveSuccess) {
      setSaveSuccess(false);
    }
  };

  const handleSave = async () => {
    if (!isValid || !hasChanges || isSaving) return;

    try {
      setIsSaving(true);
      const parsed = JSON.parse(content) as CatalogContent;
      await onSave(parsed);

      // Update initial value reference after successful save
      initialValueRef.current = content;
      setHasChanges(false);
      setSaveSuccess(true);

      // Reset success indicator after 2 seconds
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Save failed:', error);
      // Error handling is done by parent component
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (!hasChanges) return;

    if (confirm('Discard all changes and reset to last saved version?')) {
      setContent(initialValueRef.current);
      setHasChanges(false);
      setIsValid(true);
      setValidationError(null);
      setSaveSuccess(false);

      // Reformat after reset
      setTimeout(() => {
        editorRef.current?.getAction('editor.action.formatDocument')?.run();
      }, 100);
    }
  };

  const handleFormat = () => {
    editorRef.current?.getAction('editor.action.formatDocument')?.run();
  };

  return (
    <div className={clsx('flex flex-col h-full', className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-between bg-gray-50 border-b border-gray-200 px-4 py-2">
        <div className="flex items-center space-x-3">
          {/* Validation Status */}
          {isValid ? (
            <div className="flex items-center text-sm text-green-600">
              <Check className="w-4 h-4 mr-1" />
              Valid JSON
            </div>
          ) : (
            <div className="flex items-center text-sm text-red-600">
              <AlertCircle className="w-4 h-4 mr-1" />
              Invalid JSON
            </div>
          )}

          {/* Unsaved Changes Indicator */}
          {hasChanges && (
            <div className="text-sm text-amber-600">
              • Unsaved changes
            </div>
          )}

          {/* Save Success */}
          {saveSuccess && (
            <div className="flex items-center text-sm text-green-600">
              <Check className="w-4 h-4 mr-1" />
              Saved!
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {/* Format Button */}
          <button
            onClick={handleFormat}
            className="px-3 py-1 text-sm text-gray-700 hover:bg-gray-200 rounded transition-colors"
            title="Format JSON (Ctrl+Shift+F)"
          >
            Format
          </button>

          {/* Reset Button */}
          <button
            onClick={handleReset}
            disabled={!hasChanges || readOnly}
            className={clsx(
              'flex items-center px-3 py-1 text-sm rounded transition-colors',
              hasChanges && !readOnly
                ? 'text-gray-700 hover:bg-gray-200'
                : 'text-gray-400 cursor-not-allowed'
            )}
            title="Reset to last saved version"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset
          </button>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={!isValid || !hasChanges || isSaving || readOnly}
            className={clsx(
              'flex items-center px-4 py-1 text-sm rounded transition-colors',
              isValid && hasChanges && !isSaving && !readOnly
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            )}
          >
            <Save className="w-4 h-4 mr-1" />
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Validation Error */}
      {validationError && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2">
          <div className="flex items-start space-x-2 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">JSON Syntax Error:</p>
              <p className="mt-1 font-mono text-xs">{validationError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        <Editor
          height="100%"
          defaultLanguage="json"
          value={content}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          options={{
            readOnly,
            minimap: { enabled: true },
            scrollBeyondLastLine: false,
            fontSize: 14,
            lineNumbers: 'on',
            renderWhitespace: 'selection',
            tabSize: 2,
            insertSpaces: true,
            formatOnPaste: true,
            formatOnType: false,
            automaticLayout: true,
            wordWrap: 'off',
            bracketPairColorization: {
              enabled: true,
            },
            // JSON-specific
            folding: true,
            foldingStrategy: 'indentation',
            showFoldingControls: 'always',
          }}
          theme="vs-light"
        />
      </div>

      {/* Footer Info */}
      <div className="bg-gray-50 border-t border-gray-200 px-4 py-2 text-xs text-gray-500">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-medium">Keyboard shortcuts:</span>
            <span className="ml-2">Ctrl+S to save</span>
            <span className="ml-3">Ctrl+Shift+F to format</span>
            <span className="ml-3">Ctrl+F to find</span>
          </div>
          {readOnly && (
            <div className="text-amber-600 font-medium">
              Read-only mode
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
